import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DatePipe } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { HospitalStateService } from 'src/app/services/hospitalstate.service';
import { environment } from 'src/environments/environment';
import { PrintSettingService } from 'src/app/pages/settings/hospital-setting/print-setting/print-setting.service';

export interface ReportColumn {
    header: string;
    field: string;
    flex: string;
    formatter?: (value: any, row?: any) => string;
}

export interface PrintReportOptions {
    title: string;
    items: any[];
    columns: ReportColumn[];
    startDate?: Date;
    endDate?: Date;
    doctorNames?: string;
}

@Injectable({
    providedIn: 'root'
})
export class ReportPrintService {
    private staticimagepath = environment.imagePath;

    constructor(
        private http: HttpClient,
        private datePipe: DatePipe,
        private hospitalData: HospitalStateService,
        private printSettingService: PrintSettingService
    ) { }

    async printReport(options: PrintReportOptions): Promise<void> {
        const hospitalSafe = this.hospitalData.getHospital();
        const settings = await this.printSettingService.getPrintSettingPromise(hospitalSafe.id);
        const template = await this.getTemplate();
        if (!template) return;

        const rowsHtml = this.buildRows(options.items, options.columns);
        const tableHtml = this.buildTable(options.title, options.columns, rowsHtml, options.startDate, options.endDate, options?.doctorNames);

        const fullImagePath = hospitalSafe?.logo ? `${this.staticimagepath}${hospitalSafe.logo}` : null;
        const defaultLogo = `${window.location.origin}/assets/images/svgs/hospital.png`;

        const replacements: Record<string, string> = {
            reportTitle: options.title,
            hospitalName: hospitalSafe?.name ?? '',
            hospitalAddress: `${hospitalSafe?.address ?? ''}, ${hospitalSafe?.city ?? ''}`,
            phone: hospitalSafe?.phone ?? '',
            regNo: hospitalSafe?.registrationNumber ? `Registration No: ${hospitalSafe.registrationNumber}` : '',
            reportSections: tableHtml,
            doctorName: '',
            qualification: '',
            email: '',
            logo: `<img src="${fullImagePath || defaultLogo}" class="logo" onerror="this.onerror=null;this.src='${defaultLogo}'"/>`
        };

        let filledTemplate = template;
        for (const key in replacements) {
            const value = replacements[key] ?? '';
            const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
            filledTemplate = filledTemplate.replace(regex, value);
        }

        // Apply settings
        if (settings) {
            const logoPos = settings.logoPosition === 'center' ? 'column' : (settings.logoPosition === 'left' ? 'row' : 'row-reverse');
            const headerClass = settings.logoPosition === 'center' ? 'center' : '';
            const headerAlignment = settings.addressAlignment || 'left';

            const styles = `
                :root {
                    --logo-size: ${settings.logoSize || '60px'};
                    --logo-position: ${logoPos};
                    --header-alignment: ${headerAlignment};
                    --hospital-name-size: ${settings.hospitalNameSize || 20}px;
                    --address-font-size: ${settings.addressFontSize || 13}px;
                    --footer-display: ${settings.showFooter ? 'block' : 'none'};
                }
            `;
            filledTemplate = filledTemplate.replace('</head>', `<style>${styles}</style></head>`).replace('{{headerClass}}', headerClass);
        } else {
            filledTemplate = filledTemplate.replace('{{headerClass}}', '');
        }

        this.openPrintWindow(filledTemplate);
    }

    private async getTemplate(): Promise<string> {
        try {
            return (await firstValueFrom(
                this.http.get('assets/print-report-template.html', { responseType: 'text' })
            ));
        } catch (error) {
            console.error('Error loading print template', error);
            return '';
        }
    }

    private buildRows(items: any[], columns: ReportColumn[]): string {
        return items.map((row, index) => {
            const cells = columns.map(col => {
                let value = row[col.field];
                if (col.formatter) {
                    value = col.formatter(value, row);
                }
                return `<div style="flex:${col.flex};">${value ?? ''}</div>`;
            }).join('');

            return `
        <div class="row" style="display:flex; text-align:left; border-bottom:1px solid #ccc; padding:6px 10px;">
          <div style="flex:0.5;">${index + 1}</div>
          ${cells}
        </div>`;
        }).join('');
    }

    private buildTable(title: string, columns: ReportColumn[], rowsHtml: string, startDate?: Date, endDate?: Date, doctorNames?: string): string {
        const dateRangeLabel = this.getDateRangeLabel(startDate, endDate);
        const headers = columns.map(col => `<div style="flex:${col.flex};">${col.header}</div>`).join('');

        return `
      <div style="text-align:center; margin-bottom:10px;">
        <h2>${title}</h2>
      </div>
      <div style="margin-bottom:10px;">
        <h4>Search for :- ${dateRangeLabel}</h4>
      <div style="margin-bottom:10px;">
        ${doctorNames ? `<h4>Doctor :- ${doctorNames}</h4>` : ''}
      </div>
      <div class="report-table" style="display:flex; flex-direction:column; border:1px solid #999; border-radius:6px; overflow:hidden;">
        <div class="header" style="display:flex; background:#f2f2f2; font-weight:600; text-align:left; border-bottom:1px solid #ccc; padding:6px 10px;">
          <div style="flex:0.5;">No</div>
          ${headers}
        </div>
        ${rowsHtml}
      </div>`;
    }

    private getDateRangeLabel(startDate?: Date, endDate?: Date): string {
        if (!startDate || !endDate) return '';
        const start = this.datePipe.transform(startDate, 'dd/MM/yyyy') ?? '';
        const end = this.datePipe.transform(endDate, 'dd/MM/yyyy') ?? '';
        return `${start} to ${end}`;
    }

    private openPrintWindow(content: string): void {
        const printFrame = document.createElement('iframe');
        printFrame.id = 'print-iframe';
        printFrame.style.display = 'none';
        document.body.appendChild(printFrame);

        const doc = printFrame.contentWindow?.document || printFrame.contentDocument;
        if (doc) {
            doc.open();
            doc.write(content);
            doc.close();

            setTimeout(() => {
                if (printFrame.contentWindow) {
                    printFrame.contentWindow.focus();
                    printFrame.contentWindow.print();
                    printFrame.contentWindow.onafterprint = () => {
                        if (printFrame.parentNode) {
                            printFrame.parentNode.removeChild(printFrame);
                        }
                    };
                } else {
                    if (printFrame.parentNode) {
                        printFrame.parentNode.removeChild(printFrame);
                    }
                }
            }, 500);
        } else {
            if (printFrame.parentNode) {
                printFrame.parentNode.removeChild(printFrame);
            }
        }

    }
}
